import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { McTopBar } from "@maersk-global/mds-react-wrapper/components-core/mc-top-bar";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McToast } from "@maersk-global/mds-react-wrapper/components-core/mc-toast";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { BulkReassign, BulkReassignResults, BulkReassignHistory } from "./BulkReassign";
import { useUser } from "../../contexts/UserContext";
import { AL } from "../../al";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./bulkupload.css";

function BulkReassignPage() {
    const navigate = useNavigate();
    const { token } = useUser();

    const [reassignComplete, setReassignComplete] = useState(false);
    const [reassignSummary,  setReassignSummary]  = useState(null);
    const [reassignResults,  setReassignResults]  = useState([]);

    const [toastVisible,    setToastVisible]    = useState(false);
    const [toastMessage,    setToastMessage]    = useState("");
    const [toastAppearance, setToastAppearance] = useState("info");

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(0);
    const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
    const HISTORY_PAGE_SIZE = 50;

    // Kept in a ref (not a useCallback dependency) so fetchHistory's identity
    // stays stable across renders — otherwise a token hydrating shortly after
    // mount (context updating from null to the real value) recreates the
    // callback and fires a duplicate request for the same page.
    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);

    const fetchHistory = useCallback(async (page) => {
        const accessToken = tokenRef.current || localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        if (!accessToken) return;

        setHistoryLoading(true);
        try {
            const endpoint = `${process.env.REACT_APP_API_BASE_URL}/bulk-reassign/history?page=${page}&page_size=${HISTORY_PAGE_SIZE}`;
            const response = await fetch(endpoint, {
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                }
            });
            if (!response.ok) throw new Error(`Failed to fetch history (HTTP ${response.status})`);
            const data = await response.json();
            setHistory(data.history || []);
            setHistoryTotalRecords(data.total || 0);
            setHistoryTotalPages(Math.max(1, Math.ceil((data.total || 0) / (data.page_size || HISTORY_PAGE_SIZE))));
        } catch (error) {
            console.error("Error fetching bulk reassign history:", error);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHistory(historyPage);
    }, [fetchHistory, historyPage]);

    const showToast = (message, appearance = "info") => {
        setToastMessage(message);
        setToastAppearance(appearance);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 4000);
    };

    const handleLogout = () => {
        AL.logoutRedirect({ post_logout_uri: window.location.origin + "/" });
    };

    const downloadTemplate = () => {
        const rows = [
            ["Parent ID", "Child ID", "old Approver mail id", "new approver mail id", "old Cost Approver mail id","new cost approver mail id","old Po creator mail id", "new PO approver mail Id"],
            ["CN-00100", "C-00001", "old.approver@vestas.com", "new.approver@vestas.com", "old.costapprover@vestas.com", "new.costapprover@vestas.com", "old.pocreator@vestas.com", "new.pocreator@vestas.com"],
        ];
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws["!cols"] = [{ wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 30 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([out], { type: "application/octet-stream" }), "bulk_reassign_template.xlsx");
        showToast("Template downloaded", "success");
    };

    const downloadResults = () => {
        if (!reassignResults.length) { showToast("No results to download", "warning"); return; }
        const exportData = reassignResults.map(r => ({
            Row: r.row,
            "Parent ID": r.parent_id,
            "Child ID": r.child_id,
            Status: r.status,
            Message: r.message,
            "Updated Fields": JSON.stringify(r.updated_fields || {})
        }));
        const ws  = XLSX.utils.json_to_sheet(exportData);
        const wb  = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Results");
        const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const ts  = new Date().toISOString().replace(/[:.]/g, "-");
        saveAs(new Blob([out], { type: "application/octet-stream" }), `bulk_reassign_results_${ts}.xlsx`);
        showToast("Results downloaded", "success");
    };

    const resetPage = () => {
        setReassignComplete(false);
        setReassignSummary(null);
        setReassignResults([]);
    };

    return (
        <div className="mds">
            {/* ── Top Bar ── */}
            <McTopBar
                product="Maersk | Unplanned Charges"
                productshort="Maersk"
                onClick={() => navigate("/")}
                style={{ paddingLeft: "20px", height: "64px", paddingRight: "20px", marginTop: "-5px", borderBottom: "1px solid #DBDBDB" }}
            >
                <div slot="actions" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <McButton onClick={() => navigate("/")} label="Go to Home" appearance="primary" fit="small" />
                    <McButton onClick={handleLogout} label="Sign Out" appearance="secondary" fit="small" />
                </div>
            </McTopBar>

            {/* ── Main ── */}
            <main className="mds-page mds-container" style={{ padding: "2rem", minHeight: "95vh", backgroundColor: "#f8f9fa" }}>

                {/* Page header */}
                <div className="bulk-page-header">
                    <div>
                        <h3 className="main-heading">Bulk Reassign</h3>
                        <p className="bulk-page-subtitle">
                            Reassign approvers and PO creators for multiple charge records at once.
                        </p>
                    </div>
                    <McButton
                        onClick={downloadTemplate}
                        label="Download Template"
                        appearance="secondary"
                        fit="small"
                    />
                </div>

                {/* ── Content ── */}
                {reassignComplete && reassignSummary
                    ? <BulkReassignResults
                        summary={reassignSummary}
                        results={reassignResults}
                        onReset={resetPage}
                        onDownload={downloadResults}
                      />
                    : <BulkReassign
                        token={token}
                        showToast={showToast}
                        onUploadDone={(summary, results) => {
                            setReassignSummary(summary);
                            setReassignResults(results);
                            setReassignComplete(true);
                            setHistoryPage(1);
                            fetchHistory(1);
                        }}
                      />
                }

                <BulkReassignHistory
                    history={history}
                    loading={historyLoading}
                    page={historyPage}
                    totalPages={historyTotalPages}
                    totalRecords={historyTotalRecords}
                    onPageChange={setHistoryPage}
                />

            </main>

            <McToast appearance={toastAppearance} open={toastVisible} close={() => setToastVisible(false)}>
                <McNotification body={toastMessage} />
            </McToast>
        </div>
    );
}

export default BulkReassignPage;
